/**
 * Read-only runtime diagnostics for Microsoft's Canvas Authoring MCP server.
 *
 * The official server is launched with `dnx` and requires a .NET SDK with a
 * major version of at least 10. This probe intentionally never invokes dnx:
 * doing so could restore a NuGet package or start an interactive auth flow.
 * The only child process it runs is `dotnet --list-sdks`.
 */

import { execFile } from "node:child_process";
import { constants as fsConstants } from "node:fs";
import { access, stat } from "node:fs/promises";
import { delimiter, extname, join } from "node:path";

export const CANVAS_AUTHORING_MIN_DOTNET_SDK_MAJOR = 10;

export type CanvasAuthoringRuntimeClassification =
  | "ready"
  | "dnx-not-found"
  | "dotnet-not-found"
  | "dotnet-sdk-probe-failed"
  | "dotnet-sdk-10-missing";

export interface CanvasAuthoringRuntimeDiagnostic {
  ok: boolean;
  classification: CanvasAuthoringRuntimeClassification;
  message: string;
  requiredDotnetSdkMajor: number;
  dnxPath: string | null;
  dotnetPath: string | null;
  installedDotnetSdkVersions: string[];
}

export interface CanvasAuthoringRuntimeProbeOptions {
  /** Environment used for PATH resolution and for `dotnet --list-sdks`. */
  env?: NodeJS.ProcessEnv;
  /** Working directory represented by an empty PATH entry. */
  cwd?: string;
  /** Primarily useful for deterministic platform-specific tests. */
  platform?: NodeJS.Platform;
  /** Timeout for the read-only SDK inventory command. */
  timeoutMs?: number;
}

interface ParsedSdkVersion {
  version: string;
  major: number;
}

const DEFAULT_SDK_PROBE_TIMEOUT_MS = 10_000;

function envValue(
  env: NodeJS.ProcessEnv,
  name: string,
  platform: NodeJS.Platform,
): string | undefined {
  const direct = env[name];
  if (direct !== undefined || platform !== "win32") return direct;

  const matchingKey = Object.keys(env).find((key) => key.toLowerCase() === name.toLowerCase());
  return matchingKey === undefined ? undefined : env[matchingKey];
}

function executableNames(
  command: string,
  env: NodeJS.ProcessEnv,
  platform: NodeJS.Platform,
): string[] {
  if (platform !== "win32" || extname(command)) return [command];

  const pathExt = envValue(env, "PATHEXT", platform) ?? ".COM;.EXE;.BAT;.CMD";
  const extensions = pathExt
    .split(";")
    .map((value) => value.trim())
    .filter(Boolean);
  return [command, ...extensions.map((extension) => `${command}${extension}`)];
}

async function isExecutableFile(candidate: string, platform: NodeJS.Platform): Promise<boolean> {
  try {
    await access(candidate, platform === "win32" ? fsConstants.F_OK : fsConstants.X_OK);
    return (await stat(candidate)).isFile();
  } catch {
    return false;
  }
}

async function resolveExecutable(
  command: string,
  env: NodeJS.ProcessEnv,
  cwd: string,
  platform: NodeJS.Platform,
): Promise<string | null> {
  const pathValue = envValue(env, "PATH", platform);
  if (!pathValue) return null;

  const pathDelimiter = platform === "win32" ? ";" : delimiter;
  const names = executableNames(command, env, platform);

  for (const rawDirectory of pathValue.split(pathDelimiter)) {
    const unquoted = rawDirectory.replace(/^"|"$/g, "");
    const directory = unquoted || cwd;
    for (const name of names) {
      const candidate = join(directory, name);
      if (await isExecutableFile(candidate, platform)) return candidate;
    }
  }

  return null;
}

function parseDotnetSdkVersions(stdout: string): ParsedSdkVersion[] {
  const versions: ParsedSdkVersion[] = [];

  for (const line of stdout.split(/\r?\n/)) {
    const trimmed = line.trim();
    const match = /^(\d+)\.\d+\.\d+(?:[-+][^\s]+)?(?:\s|$)/.exec(trimmed);
    if (!match?.[1]) continue;
    const version = trimmed.split(/\s+/, 1)[0];
    if (!version) continue;
    versions.push({ version, major: Number.parseInt(match[1], 10) });
  }

  return versions;
}

function runSdkInventory(
  dotnetPath: string,
  env: NodeJS.ProcessEnv,
  timeoutMs: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      execFile(
        dotnetPath,
        ["--list-sdks"],
        {
          encoding: "utf8",
          env,
          maxBuffer: 1024 * 1024,
          timeout: timeoutMs,
          windowsHide: true,
        },
        (error, stdout) => {
          if (error) reject(new Error(error.message, { cause: error }));
          else resolve(stdout);
        },
      );
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

function diagnostic(
  classification: CanvasAuthoringRuntimeClassification,
  message: string,
  dnxPath: string | null,
  dotnetPath: string | null,
  installedDotnetSdkVersions: string[] = [],
): CanvasAuthoringRuntimeDiagnostic {
  return {
    ok: classification === "ready",
    classification,
    message,
    requiredDotnetSdkMajor: CANVAS_AUTHORING_MIN_DOTNET_SDK_MAJOR,
    dnxPath,
    dotnetPath,
    installedDotnetSdkVersions,
  };
}

function firstErrorLine(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.split(/\r?\n/, 1)[0] || "unknown error";
}

/**
 * Check whether the host can launch the official Canvas Authoring MCP server.
 *
 * This is safe to use from setup/doctor paths: dnx is resolved but never
 * executed, and the sole subprocess (`dotnet --list-sdks`) is noninteractive.
 */
export async function diagnoseCanvasAuthoringRuntime(
  options: CanvasAuthoringRuntimeProbeOptions = {},
): Promise<CanvasAuthoringRuntimeDiagnostic> {
  const env = options.env ?? process.env;
  const cwd = options.cwd ?? process.cwd();
  const platform = options.platform ?? process.platform;
  const timeoutMs = options.timeoutMs ?? DEFAULT_SDK_PROBE_TIMEOUT_MS;

  const dnxPath = await resolveExecutable("dnx", env, cwd, platform);
  if (!dnxPath) {
    return diagnostic(
      "dnx-not-found",
      "Canvas authoring is unavailable because dnx was not found on PATH. Install the .NET 10 SDK, then restart this process so its PATH is refreshed.",
      null,
      null,
    );
  }

  const dotnetPath = await resolveExecutable("dotnet", env, cwd, platform);
  if (!dotnetPath) {
    return diagnostic(
      "dotnet-not-found",
      "Canvas authoring is unavailable because dotnet was not found on PATH. Install the .NET 10 SDK, then restart this process so its PATH is refreshed.",
      dnxPath,
      null,
    );
  }

  let stdout: string;
  try {
    stdout = await runSdkInventory(dotnetPath, env, timeoutMs);
  } catch (error) {
    return diagnostic(
      "dotnet-sdk-probe-failed",
      `Canvas authoring runtime check failed while running dotnet --list-sdks: ${firstErrorLine(error)}`,
      dnxPath,
      dotnetPath,
    );
  }

  const installedSdks = parseDotnetSdkVersions(stdout);
  const installedVersions = installedSdks.map(({ version }) => version);
  if (!installedSdks.some(({ major }) => major >= CANVAS_AUTHORING_MIN_DOTNET_SDK_MAJOR)) {
    const found = installedVersions.length > 0 ? installedVersions.join(", ") : "none";
    return diagnostic(
      "dotnet-sdk-10-missing",
      `Canvas authoring requires .NET SDK ${CANVAS_AUTHORING_MIN_DOTNET_SDK_MAJOR} or newer; installed SDKs: ${found}.`,
      dnxPath,
      dotnetPath,
      installedVersions,
    );
  }

  return diagnostic(
    "ready",
    `Canvas authoring runtime is ready (.NET SDK ${CANVAS_AUTHORING_MIN_DOTNET_SDK_MAJOR}+ and dnx found).`,
    dnxPath,
    dotnetPath,
    installedVersions,
  );
}
